import pinkMarker from "./assets/marker-pink.png";
import purpleMarker from "./assets/marker-purple.png";
import cyanMarker from "./assets/marker-cyan.png";
import { PAGINATION_LIMIT, USER_ROLE_LABELS } from "./constants";

const dateFormatter = new Intl.DateTimeFormat("fr-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const timeFormatter = new Intl.DateTimeFormat("fr", {
  timeStyle: "short",
  timeZone: "UTC",
});

export const dateToHHMM = (utcDateString: string): string => {
  if (!utcDateString) return "-";

  const parts = utcDateString.split(' ');
  // Further split the date and time
  const dateParts = parts[0].split('-').map(part => parseInt(part, 10));
  const timeParts = parts[1].split(':').map(part => parseInt(part, 10));
  // Create the date object using Date.UTC
  const date = new Date(Date.UTC(dateParts[0], dateParts[1] - 1, dateParts[2], timeParts[0], timeParts[1], timeParts[2] || 0));

  let hours = date.getUTCHours();
  let minutes = date.getUTCMinutes();

  const hoursStr: string = hours < 10 ? '0' + hours : hours.toString();
  const minutesStr: string = minutes < 10 ? '0' + minutes : minutes.toString();

  return `${hoursStr}:${minutesStr}`;
}

export const subtractTime = (start: Date, end: Date) => {
  const minutesDiff = Math.abs(
    (new Date(end).getTime() - new Date(start).getTime()) / 60000
  );
  const hours = ~~(minutesDiff / 60);
  const minutes = ~~(minutesDiff % 60);
  return `${hours}h ${minutes}m`;
};

export const isDateValid = (date: Date) =>
  new Date(date).toString() !== "Invalid Date";

export const formatDate = (date: Date) => dateFormatter.format(date);

export const formatTime = (date: Date) =>
  date ? timeFormatter.format(new Date(date)) : "";

export const formatDateTime = (date: Date) => {
  const dateObj = new Date(date);

  return dateObj.toLocaleString("fr", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZone: "UTC",
    hour12: false,
  });
};

const appendParam = (
  params: URLSearchParams,
  key: string,
  value: string | number | null | undefined,
) => {
  if (value === null || value === undefined || `${value}`.trim() === "") {
    return;
  }

  params.set(key, `${value}`);
};

const formatFilterDate = (date: any) => {
  if (!date || !isDateValid(date)) {
    return null;
  }

  return formatDate(typeof date.toDate === "function" ? date.toDate() : date);
};

// Builds queries from params: ?flight_origin_data=2023-04-20&departure_airport=LFQQ&arrival_airport=LSZH...
export const buildFilterQueryParams = (filters: Filters, page: number) => {
  const {
    date,
    dateFrom,
    dateTo,
    search,
    callsign,
    registration,
    departureAirport,
    arrivalAirport,
    flightID,
    commercialFlightNumber,
    tlc,
    operatorRef,
  } = filters;

  const params = new URLSearchParams({
    limit: `${PAGINATION_LIMIT}`,
    offset: `${(page - 1) * PAGINATION_LIMIT}`,
  });

  appendParam(params, "flight_origin_data", formatFilterDate(date));
  appendParam(params, "dateFrom", formatFilterDate(dateFrom));
  appendParam(params, "dateTo", formatFilterDate(dateTo));
  appendParam(params, "search", search);
  appendParam(params, "flight_identifier", callsign);
  appendParam(params, "aircraft_registration", registration);
  appendParam(params, "departure_airport", departureAirport);
  appendParam(params, "arrival_airport", arrivalAirport);
  appendParam(params, "flightID", flightID);
  appendParam(params, "commercial_flight_number", commercialFlightNumber);
  appendParam(params, "user_tlc", tlc);
  appendParam(params, "operatorRef", operatorRef);
  appendParam(params, "atcType", filters.atcType);
  appendParam(params, "flightPurpose", filters.flightPurpose);
  appendParam(params, "purposeReview", filters.purposeReview);

  return `?${params.toString()}`;
};

export const buildDayFlightQueryParams = (filters: Filters) => {
  const {
    date,
    dateFrom,
    dateTo,
    search,
    callsign,
    registration,
    departureAirport,
    arrivalAirport,
    flightID,
    commercialFlightNumber,
    tlc,
    operatorRef,
  } = filters;

  const params = new URLSearchParams({
    paginationMode: "day",
    dayLimit: "1",
  });

  appendParam(params, "flight_origin_data", formatFilterDate(date));
  appendParam(params, "dateFrom", formatFilterDate(dateFrom));
  appendParam(params, "dateTo", formatFilterDate(dateTo));
  appendParam(params, "search", search);
  appendParam(params, "flight_identifier", callsign);
  appendParam(params, "aircraft_registration", registration);
  appendParam(params, "departure_airport", departureAirport);
  appendParam(params, "arrival_airport", arrivalAirport);
  appendParam(params, "flightID", flightID);
  appendParam(params, "commercial_flight_number", commercialFlightNumber);
  appendParam(params, "user_tlc", tlc);
  appendParam(params, "operatorRef", operatorRef);
  appendParam(params, "atcType", filters.atcType);
  appendParam(params, "flightPurpose", filters.flightPurpose);
  appendParam(params, "purposeReview", filters.purposeReview);

  return `?${params.toString()}`;
};

export const normaliseCoordinates = (
  coordinates?: Coordinates | null
): Coordinates | null => {
  if (!coordinates) {
    return null;
  }

  const lat =
    typeof coordinates.lat === "string"
      ? Number.parseFloat(coordinates.lat)
      : coordinates.lat;
  const lng =
    typeof coordinates.lng === "string"
      ? Number.parseFloat(coordinates.lng)
      : coordinates.lng;

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return { lat, lng };
};

export const calculatePath = (data: Waypoint[]) => {
  const path = [];

  for (let i = 0; i < data.length; i += 1) {
    path.push(data[i].coordinates);
  }

  return path;
};

export const calculateCenter = (data: Waypoint[]) => {
  if (!data.length) return;
  const coordsFirstArr = data[0].coordinates;
  const coordsLastArr = data[data.length - 1].coordinates;
  return {
    lat: (coordsFirstArr.lat + coordsLastArr.lat) / 2,
    lng: (coordsFirstArr.lng + coordsLastArr.lng) / 2,
  };
};

export const calculateOverallCenter = (segments: Waypoint[][]) => {
  const points = segments.flatMap((segment) => calculatePath(segment));

  if (!points.length) {
    return undefined;
  }

  const { lat, lng } = points.reduce(
    (acc, point) => ({
      lat: acc.lat + point.lat,
      lng: acc.lng + point.lng,
    }),
    { lat: 0, lng: 0 }
  );

  return {
    lat: lat / points.length,
    lng: lng / points.length,
  };
};

export const getMarker = (isAfqSet: boolean, isAtoSet: boolean) => {
  if (isAtoSet && isAfqSet) {
    return cyanMarker;
  }

  if (isAtoSet || isAfqSet) {
    return pinkMarker;
  }

  return purpleMarker;
};

export const getLabelColor = (isAfqSet: boolean, isAtoSet: boolean) => {
  if (isAtoSet && isAfqSet) {
    return "label-cyan";
  }

  if (isAtoSet || isAfqSet) {
    return "label-pink";
  }

  return "label-purple";
};

type OperatorLike = {
  booked_options?: unknown;
  bookedOptions?: unknown;
  settings?: {
    booked_options?: unknown;
    bookedOptions?: unknown;
  } | null;
} | null | undefined;

const normaliseBookedOptions = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((option): option is string => typeof option === "string");
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return normaliseBookedOptions(parsed);
      }
    } catch (error) {
      const parts = value
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);

      if (parts.length) {
        return parts;
      }
    }
  }

  return [];
};

const extractBookedOptions = (operator?: OperatorLike | null): string[] => {
  if (!operator) {
    return [];
  }

  const directOptions = normaliseBookedOptions(operator.booked_options);
  if (directOptions.length) {
    return directOptions;
  }

  const camelCaseOptions = normaliseBookedOptions(operator.bookedOptions);
  if (camelCaseOptions.length) {
    return camelCaseOptions;
  }

  const settingsOptions = normaliseBookedOptions(operator.settings?.booked_options);
  if (settingsOptions.length) {
    return settingsOptions;
  }

  const camelCaseSettingsOptions = normaliseBookedOptions(
    operator.settings?.bookedOptions
  );

  if (camelCaseSettingsOptions.length) {
    return camelCaseSettingsOptions;
  }

  return [];
};

export const getOperatorBookedOptions = (operator?: OperatorLike): string[] =>
  extractBookedOptions(operator);

export const getNormalizedUserRole = (
  role?: string | null,
  isAdmin?: boolean
): UserRole => {
  if (
    role === "super_admin" ||
    role === "company_admin" ||
    role === "user" ||
    role === "acmi_client" ||
    role === "refuel_eu_reader"
  ) {
    return role;
  }

  return isAdmin ? "super_admin" : "user";
};

export const getAssignableRoles = (currentRole: UserRole): UserRole[] => {
  if (currentRole === "super_admin") {
    return ["super_admin", "company_admin", "user", "acmi_client", "refuel_eu_reader"];
  }

  if (currentRole === "company_admin") {
    return ["company_admin", "user", "acmi_client", "refuel_eu_reader"];
  }

  return ["user"];
};

export const getUserTypeLabel = (type?: number | null) => {
  switch (type) {
    case 0:
      return "smartEFB";
    case 1:
      return "FlightViewer";
    case 2:
      return "smartEFB + FlightViewer";
    default:
      return "";
  }
};

export const getRoleLabel = (role?: string | null, isAdmin?: boolean) =>
  USER_ROLE_LABELS[getNormalizedUserRole(role, isAdmin)];

export const getRoleDisplay = (
  role?: string | null,
  type?: number | null,
  isAdmin?: boolean
) => {
  const normalizedRole = getNormalizedUserRole(role, isAdmin);
  const roleLabel = USER_ROLE_LABELS[normalizedRole];

  if (normalizedRole === "user" || normalizedRole === "acmi_client") {
    const typeLabel = getUserTypeLabel(type);
    return typeLabel ? `${roleLabel} (${typeLabel})` : roleLabel;
  }

  return roleLabel;
};

export const AUTH_STATE_CHANGED_EVENT = "flightviewer:auth-state-changed";

export type AuthSnapshot = {
  isAuthenticated: boolean;
  userData: any;
  userRaw: string | null;
};

const parseStoredUser = (raw: string | null) => {
  if (!raw) {
    return {};
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return {};
  }
};

export const getStoredAuthSnapshot = (): AuthSnapshot => {
  const userRaw = localStorage.getItem("user");
  return {
    isAuthenticated: localStorage.getItem("isAuthenticated") === "true",
    userData: parseStoredUser(userRaw),
    userRaw,
  };
};

export const notifyAuthStateChanged = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_STATE_CHANGED_EVENT));
  }
};

export const setStoredAuthUser = (user: unknown) => {
  localStorage.setItem("user", JSON.stringify(user ?? {}));
  localStorage.setItem("isAuthenticated", "true");
  notifyAuthStateChanged();
};

export const clearStoredAuth = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("isAuthenticated");
  notifyAuthStateChanged();
};

export const mergeStoredAuthUser = (partialUser: Record<string, unknown> | null | undefined) => {
  const snapshot = getStoredAuthSnapshot();
  setStoredAuthUser({
    ...snapshot.userData,
    ...(partialUser || {}),
  });
};

export const getStoredBookedOptions = (userData: unknown): string[] =>
  extractBookedOptions((userData || null) as OperatorLike);

export const isAuthenticated = () =>
  localStorage.getItem("isAuthenticated") === "true";

export const validateEmail = (email: string) => {
  const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  return regex.test(email);
};

export const move = (source: any[], destination: any[], droppableSource: any, droppableDestination: any): { [key: string]: any; } => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result: { [key: string]: any; } = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

export const sortFiles = (files: string[] = []) => {
  const lower = (value: string) => value.toLowerCase();
  const generatedPdfKeywords = [
    "flightdata",
    "flightlog",
    "notes",
    "rvsm_data",
    "weathernotam",
  ];
  const isGeneratedPdf = (fileName: string) =>
    generatedPdfKeywords.some((keyword) => lower(fileName).includes(keyword));

  const legacySummaryPattern = /^\d+_[A-Z0-9-]+-\d{2}-\d{2}-\d{2} \d{2}:\d{2}-[A-Z]{4}-[A-Z]{4}\.pdf$/;
  const registrationSummaryPattern = /^[A-Z0-9-]+-\d{2}-\d{2}-\d{2} \d{2}:\d{2}-[A-Z]{4}-[A-Z]{4}\.pdf$/;
  const correctedSummaryPattern = /^.+_corrected_.+\.pdf$/i;

  const flightSummaryFiles = files.filter((file: string) => {
    if (!lower(file).endsWith(".pdf") || isGeneratedPdf(file)) {
      return false;
    }

    return (
      file.includes("_perfo_") ||
      legacySummaryPattern.test(file) ||
      registrationSummaryPattern.test(file) ||
      correctedSummaryPattern.test(file)
    );
  });

  const flightGeneratedPDFs = files.filter((file: string) => isGeneratedPdf(file));
  const ppsFiles = files.filter(
    (file: string) => file.includes('PPS') || /(^|\/)SKY_/.test(file),
  );
  const otherFiles = files.filter((file: string) =>
    !flightSummaryFiles.includes(file) &&
    !flightGeneratedPDFs.includes(file) &&
    !ppsFiles.includes(file)
  );

  return { flightSummaryFiles, flightGeneratedPDFs, ppsFiles, otherFiles }
}
